/**
 * useSiteComments — React Query hooks for the Wave 4 / M2 site_comments
 * surface. Mirrors the ticket comments hooks (`useTicketComments`,
 * `useCreateComment`, `useDeleteComment`) plus an `useUpdateSiteComment`
 * for the new edit flow.
 *
 * Cache keys are nested under `siteKeys.detail(siteId)` so invalidating a
 * site's detail cascades to its comment thread, the same pattern used for
 * ticket comments.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { siteComments } from '../api/methods';
import { siteKeys } from './useSites';
export const siteCommentKeys = {
    all: ['siteComments'],
    list: (siteId) => [...siteKeys.detail(String(siteId)), 'comments'],
};
export function useSiteComments(siteId) {
    return useQuery({
        queryKey: siteCommentKeys.list(siteId),
        queryFn: () => siteComments.list(siteId),
        enabled: siteId !== '' && siteId != null,
    });
}
export function useCreateSiteComment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ siteId, body }) => siteComments.create(siteId, body),
        onSuccess: (newComment, { siteId }) => {
            queryClient.setQueryData(siteCommentKeys.list(siteId), (old = []) => {
                const index = old.findIndex((comment) => comment.id === newComment.id);
                if (index === -1)
                    return [...old, newComment];
                const merged = [...old];
                merged[index] = newComment;
                return merged;
            });
            // Bump the site detail so any embedded comment counters refresh.
            queryClient.invalidateQueries({
                queryKey: siteKeys.detail(String(siteId)),
            });
        },
    });
}
export function useUpdateSiteComment() {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ siteId, commentId, body }) => siteComments.update(siteId, commentId, body),
        onSuccess: (updated, { siteId }) => {
            queryClient.setQueryData(siteCommentKeys.list(siteId), (old = []) => old.map((c) => (c.id === updated.id ? updated : c)));
        },
    });
}
export function useDeleteSiteComment(defaultSiteId) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (vars) => {
            if (typeof vars === 'string') {
                if (defaultSiteId == null || defaultSiteId === '') {
                    return Promise.reject(new Error('useDeleteSiteComment: pass { siteId, commentId } or call useDeleteSiteComment(siteId) first'));
                }
                return siteComments.remove(defaultSiteId, vars);
            }
            return siteComments.remove(vars.siteId, vars.commentId);
        },
        onSuccess: (_data, vars) => {
            const commentId = typeof vars === 'string' ? vars : vars.commentId;
            const siteId = typeof vars === 'string' ? defaultSiteId : vars.siteId;
            if (siteId != null && siteId !== '') {
                queryClient.setQueryData(siteCommentKeys.list(siteId), (old = []) => old.filter((c) => c.id !== commentId));
                queryClient.invalidateQueries({
                    queryKey: siteKeys.detail(String(siteId)),
                });
            }
            else {
                queryClient.invalidateQueries({ queryKey: siteCommentKeys.all });
            }
        },
    });
}
//# sourceMappingURL=useSiteComments.js.map